import { useState, useEffect } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Divider,
  Alert,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { LicenseSkillAssignment, LicenseTemplate, LicenseKey } from '../types';
import { toExpiryDateByDays } from '../utils/license-utils';
import SkillSelectionPanel from '@/features/license-keys/components/SkillSelectionPanel';

interface LicenseBatchDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onBatchCreate: (payload: {
    count: number;
    max_activations?: number;
    expires_at?: string;
    notes?: string;
    prefix?: string;
    template?: string;
  }) => Promise<LicenseKey[]>;
  onAssociateSkills: (licenseKey: string, skillIds: string[]) => Promise<void>;
  availableSkills: LicenseSkillAssignment[];
  selectedSkills: string[];
  loadingSkills: boolean;
  onToggleSkill: (skillId: string, checked: boolean) => void;
  templates: LicenseTemplate[];
  loadingTemplates: boolean;
  onRefresh: () => void;
}

/**
 * 批量许可证创建对话框
 */
export default function LicenseBatchDialog({
  open,
  onClose,
  onSuccess,
  onError,
  onBatchCreate,
  onAssociateSkills,
  availableSkills,
  selectedSkills,
  loadingSkills,
  onToggleSkill,
  templates,
  loadingTemplates,
  onRefresh,
}: LicenseBatchDialogProps) {
  const { t } = useTranslation();

  const [batchCount, setBatchCount] = useState(5);
  const [batchPrefix, setBatchPrefix] = useState('');
  const [maxActivations, setMaxActivations] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 对话框打开时重置表单
  useEffect(() => {
    if (open) {
      setBatchCount(5);
      setBatchPrefix('');
      setMaxActivations(1);
      setExpiresAt('');
      setNotes('');
      setSelectedTemplate('');
      setError('');
    }
  }, [open]);

  const handleTemplateChange = (event: SelectChangeEvent<string>) => {
    const templateId = event.target.value;
    setSelectedTemplate(templateId);

    if (!templateId) {
      return;
    }

    const template = templates.find((item) => item.id.toString() === templateId);
    if (!template) {
      return;
    }

    setMaxActivations(template.max_activations);
    setExpiresAt(toExpiryDateByDays(template.validity_days));

    const templateNotes = template.description
      ? `${template.display_name}: ${template.description}`
      : template.display_name;

    setNotes(templateNotes);
  };

  const selectedTemplateModel = templates.find(
    (template) => template.id.toString() === selectedTemplate
  );

  const handleSubmit = async () => {
    setError('');

    if (batchCount < 1 || batchCount > 100) {
      setError(t('licenses.countRangeError'));
      return;
    }

    const payload: {
      count: number;
      max_activations?: number;
      expires_at?: string;
      notes?: string;
      prefix?: string;
      template?: string;
    } = { count: batchCount };

    if (maxActivations) {
      payload.max_activations = maxActivations;
    }
    if (expiresAt) {
      payload.expires_at = expiresAt;
    }
    if (notes) {
      payload.notes = notes;
    }
    if (batchPrefix) {
      payload.prefix = batchPrefix;
    }

    if (selectedTemplate) {
      const template = templates.find((item) => item.id.toString() === selectedTemplate);
      if (template) {
        payload.template = template.name;
      }
    }

    setLoading(true);
    try {
      const createdLicenses = await onBatchCreate(payload);

      if (selectedSkills.length > 0 && !selectedTemplate) {
        await Promise.all(
          createdLicenses.map((license) =>
            onAssociateSkills(license.key, selectedSkills).catch((skillsError) => {
              console.error(`Failed to associate skills for license ${license.key}:`, skillsError);
            })
          )
        );
      }

      onSuccess(t('licenses.batchCreatedSuccess', { count: createdLicenses.length }));
      onClose();
      onRefresh();
    } catch (requestError) {
      const err = requestError as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || t('licenses.createFailed');
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('licenses.batchCreateTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth
            type="number"
            label={t('licenses.count')}
            value={batchCount}
            onChange={(event) => setBatchCount(parseInt(event.target.value, 10) || 1)}
            inputProps={{ min: 1, max: 100 }}
            helperText={t('licenses.countHint')}
          />

          <TextField
            fullWidth
            label={t('licenses.prefix')}
            value={batchPrefix}
            onChange={(event) => setBatchPrefix(event.target.value.toUpperCase())}
            inputProps={{ maxLength: 3 }}
            helperText={t('licenses.prefixHint')}
          />

          <TextField
            fullWidth
            type="number"
            label={t('licenses.maxActivationsPerKey')}
            value={maxActivations}
            onChange={(event) => setMaxActivations(parseInt(event.target.value, 10) || 1)}
            inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth
            type="date"
            label={t('licenses.expirationDate')}
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={t('licenses.expirationDateHint')}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('licenses.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Template
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Optional: use a predefined template to fill expiration and defaults.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={selectedTemplate}
                label="Template"
                onChange={handleTemplateChange}
                disabled={loadingTemplates || templates.length === 0}
              >
                <MenuItem value="">
                  <em>No template</em>
                </MenuItem>
                {templates
                  .filter((template) => template.is_active)
                  .map((template) => (
                    <MenuItem key={template.id} value={template.id.toString()}>
                      {template.display_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {selectedTemplateModel && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max activations: {selectedTemplateModel.max_activations}
                {selectedTemplateModel.validity_days
                  ? ` | Validity: ${selectedTemplateModel.validity_days} days`
                  : ''}
              </Typography>
            )}
          </Box>

          {!selectedTemplate && (
            <>
              <Divider sx={{ my: 1 }} />
              <SkillSelectionPanel
                title="Enabled Skills"
                description="Apply the same skills to all newly generated licenses."
                skills={availableSkills}
                selectedSkillIds={selectedSkills}
                loading={loadingSkills}
                onToggleSkill={onToggleSkill}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('licenses.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('licenses.generateBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
